import {
  format,
  formatDistanceToNowStrict,
  isThisYear,
  isToday,
  isValid,
  parseISO,
} from 'date-fns';

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? parseISO(value) : value;
  return isValid(date) ? date : null;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  if (isToday(date)) return `Today ${format(date, 'HH:mm')}`;
  return format(date, isThisYear(date) ? 'd MMM HH:mm' : 'd MMM yyyy HH:mm');
}

export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return format(date, isThisYear(date) ? 'd MMM' : 'd MMM yyyy');
}

export function formatTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'HH:mm') : '—';
}

export function relativeTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  const suffix = date.getTime() > Date.now() ? 'from now' : 'ago';
  return `${formatDistanceToNowStrict(date)} ${suffix}`;
}

/** Turn SCREAMING_SNAKE into "Screaming snake" for display. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatNumber(value: number | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const rendered = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
  return unit ? `${rendered} ${unit}` : rendered;
}

export function formatPercent(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${Math.round(value)}%`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Compact device descriptor recorded against a checklist run. */
export function deviceInfo(): string {
  const ua = navigator.userAgent;
  const platform =
    /iPhone|iPad|iPod/i.test(ua) ? 'iOS' :
    /Android/i.test(ua) ? 'Android' :
    /Windows/i.test(ua) ? 'Windows' :
    /Mac/i.test(ua) ? 'macOS' : 'Other';
  return `${platform} · ${window.screen.width}x${window.screen.height}`;
}

/** Current shift by clock time. A/B/C on an 8-hour rotation from 06:00. */
export function currentShift(): 'A' | 'B' | 'C' {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 14) return 'A';
  if (hour >= 14 && hour < 22) return 'B';
  return 'C';
}
